import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Replaces an eligible cart line with its matching Special Kit.
 *
 * @extends {Component}
 */
class CartSpecialKitUpgrade extends Component {
  requiredRefs = ['button', 'error', 'liveRegion'];

  async upgrade(event) {
    event.preventDefault();

    const button = this.refs.button;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const kitVariantId = this.dataset.kitVariantId;
    const lineKey = this.dataset.lineKey;
    const quantity = Math.max(parseInt(this.dataset.quantity || '1', 10), 1);

    if (!kitVariantId || !lineKey) return;

    this.#setError('');
    button.disabled = true;
    button.textContent = button.dataset.loadingText || button.textContent;

    try {
      const sectionIds = this.#cartSectionIds();
      const formData = new FormData();
      formData.append('id', kitVariantId);
      formData.append('quantity', String(quantity));
      formData.append('properties[_lv_special_kit_upgrade]', 'true');
      formData.append('properties[_lv_special_kit_upgrade_from]', lineKey);

      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const addConfig = fetchConfig('javascript', { body: formData });
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        ...addConfig,
        headers: {
          ...addConfig.headers,
          Accept: 'text/html',
        },
      });
      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        throw new Error(addData.description || addData.message || 'Cart add failed');
      }

      const changeBody = {
        id: lineKey,
        quantity: 0,
      };

      if (sectionIds.length) {
        changeBody.sections = sectionIds.join(',');
        changeBody.sections_url = window.location.pathname;
      }

      const changeResponse = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
        body: JSON.stringify(changeBody),
      }));
      const changeData = await changeResponse.json();

      if (!changeResponse.ok || changeData.errors) {
        throw new Error(changeData.description || changeData.errors || 'Cart update failed');
      }

      this.#announce(button.dataset.defaultText || '');
      document.dispatchEvent(
        new CartUpdateEvent(changeData, 'cart-special-kit-upgrade', {
          itemCount: changeData.item_count,
          sections: changeData.sections || {},
          source: 'cart-special-kit-upgrade',
        })
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Cart update failed';
      this.#setError(message);
      button.disabled = false;
      button.textContent = button.dataset.defaultText || button.textContent;
    }
  }

  #cartSectionIds() {
    return [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((item) => (item instanceof HTMLElement ? item.dataset.sectionId : ''))
          .filter(Boolean)
      ),
    ];
  }

  #setError(message) {
    const error = this.refs.error;
    if (!(error instanceof HTMLElement)) return;

    error.textContent = message;
    error.hidden = !message;
    this.#announce(message);
  }

  #announce(message) {
    const liveRegion = this.refs.liveRegion;
    if (liveRegion instanceof HTMLElement) {
      liveRegion.textContent = message;
    }
  }
}

if (!customElements.get('cart-special-kit-upgrade')) {
  customElements.define('cart-special-kit-upgrade', CartSpecialKitUpgrade);
}
